import React, { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import sanitizeHtml from 'sanitize-html';
import NextImage from 'next/image';
import dayjs from 'dayjs';

import { getClient } from '@graphql/graphql-client/graphClient';
import {
    Visibility,
    useAssignmentTemplatesQuery,
    useQuizTemplatesQuery,
} from '@graphql/generated-graphql';

import { capitalizeFirstLetter } from '@utils/capitalizeFirst';
import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';

import { WhitePanel } from '@components/common/panels/WhitePanel';
import { StudentFacingAssignment } from '@components/activities/StudentFacingAssignment';
import {
    Button,
    MenuContainer,
    MenuFieldItem,
    MenuItemContainer,
    SkipperAvatar,
} from '@skipper/ui';
import { UseActivityTemplateModal } from '@components/modals/useActivityTemplateModal/UseActivityTemplateModal';

import { QuizIconSVG } from '@components/common/svgs/QuizIconSVG';
import { useEnrollHandler } from '@hooks/useEnrollmentsWithMapping';
import { Menu } from '@headlessui/react';

import { faEllipsis } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useUserSession } from '@hooks/user/useDefaultSession';
import { Modal } from '@components/base';
import { useDeleteTemplate } from '@hooks/modals/useDeleteTemplate';
import { useQueryClient } from 'react-query';
import { QuizImageArray } from '@components/activities/sharedItems';
import { SimpleSpinner } from '@components/common/loading/SimpleSpinner';

export default function QuizTemplatePage() {
    const router = useRouter();
    const [user] = useUserSession();
    const { query } = router;
    const { id } = query;
    const { toggle } = useUniversalModalDispatch();

    const { data, isLoading, isFetching } = useQuizTemplatesQuery(getClient('templates'), {
        ids: [id.toString()],
    });

    const [template] = data?.quizTemplates ?? [];

    const {
        enrolls: { leadingCourses },
        loading: coursesDataLoading,
    } = useEnrollHandler();

    const queryClient = useQueryClient();

    const onQuizSuccess = (data) => {
        queryClient.invalidateQueries('QuizTemplates', {
            refetchInactive: true,
            refetchActive: true,
        });
        router.push('/templates');
    };

    const modalParams = useDeleteTemplate({
        // onCourseSuccess,
        // onAssignmentSuccess,
        onQuizSuccess,
        // onUnitSuccess,
    });

    const { deleteModalProps } = modalParams;

    if (isLoading)
        return (
            <div className="py-20">
                <SimpleSpinner />
            </div>
        );

    return (
        <div className="w-full h-full overflow-scroll">
            <div className="flex justify-center w-full p-20">
                <WhitePanel
                    className="w-[70%] max-w-[800px] mb-0 h-auto pt-8"
                    ignorePadding
                    ignoreVertical>
                    <div className="px-6">
                        <p
                            className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer font-500"
                            onClick={() => router.push('/templates')}>
                            &lt; Back to templates
                        </p>
                        <div className="flex justify-between gap-x-2 mt-2">
                            <div className="flex gap-x-2 items-center mt-2">
                                <QuizIconSVG className="w-6 h-6" />
                                <p className="text-2xl font-medium">{template?.title}</p>
                            </div>
                            <div className="flex gap-x-2 items-center mt-2">
                                <Button
                                    size="medium"
                                    variant="primary"
                                    className="py-2"
                                    type="button"
                                    onClick={() =>
                                        toggle(
                                            true,
                                            <UseActivityTemplateModal
                                                originalCourseId={template?.templateInCourse?.id}
                                                templateTitle={template?.title}
                                                templateId={parseInt(template?.id)}
                                                coursesList={leadingCourses}
                                                setLoad={() => {}}
                                                isQuiz
                                            />,
                                        )
                                    }>
                                    Use template
                                </Button>
                                <>
                                    {user?.id === template?.author?.id && (
                                        <Menu as={MenuContainer}>
                                            <Menu.Button
                                                as={Button}
                                                variant="textOnlyDark"
                                                size="medium"
                                                className="gap-x-2">
                                                <Button
                                                    size="medium"
                                                    variant="white"
                                                    className="py-2"
                                                    type="button"
                                                    onClick={() => {}}>
                                                    <FontAwesomeIcon icon={faEllipsis} />
                                                </Button>
                                            </Menu.Button>
                                            <Menu.Items as={MenuItemContainer} css={{ mt: '0px' }}>
                                                {/* <Menu.Item>
                                            {({ active }) => {
                                                return (
                                                    <MenuFieldItem
                                                        active={active}
                                                        onClick={() => {}}>
                                                        <div className="flex w-full items-center gap-x-2">
                                                            Edit template
                                                        </div>
                                                    </MenuFieldItem>
                                                );
                                            }}
                                        </Menu.Item> */}
                                                <Menu.Item>
                                                    {({ active }) => {
                                                        return (
                                                            <MenuFieldItem
                                                                active={active}
                                                                onClick={modalParams.onClick}>
                                                                <div className="flex w-full items-center gap-x-2">
                                                                    Delete template
                                                                </div>
                                                            </MenuFieldItem>
                                                        );
                                                    }}
                                                </Menu.Item>
                                            </Menu.Items>
                                        </Menu>
                                    )}
                                </>
                            </div>
                        </div>
                    </div>
                    <TemplateContent template={template} />
                </WhitePanel>
            </div>
            <Modal
                onConfirm={() => {
                    modalParams.onConfirm(parseInt(template?.id), 'QuizTemplate');
                }}
                onCancel={modalParams.onCancel}
                open={modalParams.open}
                setOpen={() => modalParams.setOpen}
                {...deleteModalProps}
            />
        </div>
    );
}

const TemplateContent = ({ template }) => {
    const templateTabs = [
        {
            id: 'questions',
            title: 'Questions',
        },
        {
            id: 'about',
            title: 'About',
        },
        // {
        //     id: 'sources',
        //     title: 'Sources',
        // },
    ];
    const [currentTab, setCurrentTab] = useState(templateTabs[0]);

    return (
        <>
            <div className="flex w-full justify-start items-center gap-x-4 border-b border-gray-200 px-6">
                {templateTabs.map((option) => (
                    <div
                        key={option.id}
                        onClick={() => setCurrentTab(option)}
                        className={`py-4 px-1 text-sm flex items-center gap-x-2 cursor-pointer ${
                            currentTab.id === option.id
                                ? 'text-teal-600 border-b-2 border-teal-600'
                                : 'text-gray-500'
                        }`}>
                        {option.title}
                    </div>
                ))}
            </div>
            <div className="w-full h-content">
                {currentTab.id === 'questions' && <QuestionsTab template={template} />}
                {currentTab.id === 'about' && <AboutTab template={template} />}
                {currentTab.id === 'sources' && <SourcesTab template={template} />}
            </div>
        </>
    );
};

const QuestionsTab = ({ template }) => {
    const ImageArray = QuizImageArray;
    const instructions = useMemo(() => {
        if (!template?.instructions) return '';
        return sanitizeHtml(template.instructions, {
            allowedTags: sanitizeHtml.defaults.allowedTags.concat(['iframe', 'img', 'p', 'span']),
            allowedAttributes: {
                ...sanitizeHtml.defaults.allowedAttributes,
                iframe: ['*'],
                img: ['*'],
                p: ['*'],
                span: ['*'],
            },
        });
    }, [template]);
    return (
        <>
            <div
                className={`px-6 ${!!instructions ? 'py-4' : ''}`}
                dangerouslySetInnerHTML={{ __html: instructions }}
            />
            <div className="flex flex-col border border-gray-200 rounded-lg">
                {template?.questions?.map((question, index) => {
                    const isEven = index % 2 === 0;
                    return (
                        <div
                            key={`question_${index}`}
                            className={`flex gap-x-3 items-center w-full py-6 px-6 ${
                                !isEven ? 'bg-white' : 'bg-gray-50'
                            }`}>
                            <NextImage
                                src={
                                    ImageArray.find(
                                        (image) => image?.blockType === question?.questionType,
                                    )?.imgMini || ''
                                }
                                layout="fixed"
                                width={25}
                                height={25}
                            />
                            {question?.questionType !== 'fill' ? (
                                <div
                                    className=""
                                    dangerouslySetInnerHTML={{
                                        __html: question?.content?.statement,
                                    }}
                                />
                            ) : (
                                <div
                                    className=""
                                    dangerouslySetInnerHTML={{ __html: question?.content?.begin }}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
};

const AboutTab = ({ template }) => {
    const router = useRouter();
    const visibleTo = useMemo(
        () =>
            template?.visibleTo === Visibility.Me
                ? 'Just me'
                : capitalizeFirstLetter(template?.visibleTo),
        [template],
    );
    const copyableBy = useMemo(
        () =>
            template?.copyableBy === Visibility.Me
                ? 'Just me'
                : capitalizeFirstLetter(template?.copyableBy),
        [template],
    );
    return (
        <>
            {/* <div className="mt-6">
                <p className="text-lg font-medium">Tags</p>
                <p className="text-sm text-gray-700">Levels</p>
                {!!template?.gradeLevels?.length ? (
                    <div className="flex flex-wrap gap-x-2 gap-y-2 mt-2">
                        {template?.gradeLevels?.map((level, index) => (
                            <span
                                key={`level_${index}`}
                                className="bg-lime-100 py-1 px-3 rounded-sm">
                                {level}
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-500">No levels</p>
                )}
                <p className="text-sm text-gray-700 mt-4">Subjects</p>
                {!!template?.subjects?.length ? (
                    <div className="flex flex-wrap gap-x-2 gap-y-2 mt-2">
                        {template?.subjects?.map((subject, index) => (
                            <span
                                key={`subject_${index}`}
                                className="bg-ocean-100 py-1 px-3 rounded-sm">
                                {subject}
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-500">No subjects</p>
                )}
                <p className="text-sm text-gray-700 mt-4">Curriculum</p>
                {!!template?.curriculum?.length ? (
                    <div className="flex flex-wrap gap-x-2 gap-y-2 mt-2 ">
                        {template?.curriculum?.map((curriculum, index) => (
                            <span
                                key={`curriculum_${index}`}
                                className="bg-sunshine-100 py-1 px-3 rounded-sm">
                                {curriculum}
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-500">No curriculum</p>
                )}
                <p className="text-sm text-gray-700 mt-4">Additional tags</p>
                {!!template?.tags?.length ? (
                    <div className="flex flex-wrap gap-x-2 gap-y-2 mt-2">
                        {template?.tags?.map((tag, index) => (
                            <span key={`tag_${index}`} className="bg-gray-100 py-1 px-3 rounded-sm">
                                {tag}
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-500">No additional tags</p>
                )}
                <p className="text-sm text-gray-700 mt-4 mb-2">Course Credits</p>
                {!!template?.courseCredits ? (
                    <span className="bg-magenta-100 py-1 px-3 rounded-sm">
                        {template?.courseCredits}{' '}
                        {template?.courseCredits > 1 ? 'credits' : 'credit'}
                    </span>
                ) : (
                    <p className="text-gray-500 -mt-2">No course credits</p>
                )}
            </div> */}
            <div className="mt-6 px-6 pb-8">
                <p className="text-lg font-medium">License</p>
                <div className="grid grid-cols-8 mt-6">
                    <div className="col-span-4 flex flex-col">
                        <p className="text-sm text-gray-700 mb-1">Visible To</p>
                        <p>{capitalizeFirstLetter(visibleTo)}</p>
                    </div>
                    <div className="col-span-4 flex flex-col">
                        <p className="text-sm text-gray-700 mb-1">Copyable by</p>
                        <p>{capitalizeFirstLetter(copyableBy)}</p>
                    </div>
                </div>
                <div className="grid grid-cols-8 mt-6">
                    <div className="col-span-4 flex flex-col">
                        <p className="text-sm text-gray-700 mb-1">Date created</p>
                        <p>{dayjs(template?.createdAt).format('MMMM D, YYYY')}</p>
                    </div>
                    <div className="col-span-4 flex flex-col">
                        <p className="text-sm text-gray-700 mb-1">Updated</p>
                        <p>{dayjs(template?.createdAt).format('MMMM D, YYYY')}</p>
                    </div>
                </div>
                <div className="grid grid-cols-8 mt-6">
                    <div className="col-span-4 flex flex-col">
                        <p className="text-sm text-gray-700 mb-1">Created by</p>
                        <div className="flex gap-x-2 items-center text-gray-700">
                            <SkipperAvatar
                                initials={template?.author?.initials}
                                calendarColor={template?.author?.calendarColor}
                                imageUrl={template?.author?.imageFileEntry?.url}
                                size="medium"
                            />
                            {template?.author?.fullName}
                        </div>
                    </div>
                    {/* <div className="col-span-4 flex flex-col">
                    <p className="text-sm text-gray-700 mb-1">Adapted from</p>
                    <p>{dayjs(template?.createdAt).format('MMMM D, YYYY')}</p>
                </div> */}
                </div>
                {/* {!!template?.originalCourse?.title && (
                <div className="mt-6">
                    <p className="text-sm text-gray-700 mb-1">Adapted from</p>
                    <span
                        className="text-teal-600 cursor-pointer hover:text-teal-700"
                        onClick={() => router.push(`/courses/${template?.originalCourse?.id}`)}>
                        {template?.originalCourse?.title}
                    </span>{' '}
                    <span>on {dayjs(template?.createdAt).format('MMMM D, YYYY')}</span>
                </div>
            )} */}
            </div>
        </>
    );
};

const SourcesTab = ({ template }) => {
    return <div></div>;
};

QuizTemplatePage.useDefault = true;
